import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Send, Plus, Trash2, Beaker, CheckCircle2, AlertCircle, Activity, ListPlus, X } from 'lucide-react';
import { Textarea } from './ui/textarea';
import ConfirmDialog from './ConfirmDialog';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';

const PROVIDER_LABEL = {
  fb_capi: 'Facebook / Meta CAPI',
  google_ads: 'Google Ads',
  tiktok_events: 'TikTok Events API',
  // Audit Phase 2 §6.2.Г — additional CAPI integrations. The wire
  // contract (cfg, event_name, click, conv) is identical to the
  // existing providers; the backend dispatcher merges them in via
  // capi_providers.PROVIDERS.
  linkedin_capi: 'LinkedIn Conversions API',
  twitter_capi: 'X / Twitter Conversions API',
  reddit_capi: 'Reddit Conversions API',
  snapchat_capi: 'Snapchat Conversions API',
  pinterest_capi: 'Pinterest Conversions API',
  yandex_metrika: 'Yandex Metrica (offline conversions)',
};

const CONFIG_FIELDS = {
  fb_capi: [
    { key: 'pixel_id', label: 'Pixel ID', required: true },
    { key: 'access_token', label: 'Access Token', required: true, secret: true },
    { key: 'test_event_code', label: 'Test event code (optional)' },
  ],
  tiktok_events: [
    { key: 'pixel_code', label: 'Pixel Code', required: true },
    { key: 'access_token', label: 'Access Token', required: true, secret: true },
    { key: 'test_event_code', label: 'Test event code (optional)' },
  ],
  // Google Ads has two UI paths, handled inline in the form below:
  //   mode=simple   → one field: conversion tag (AW-XXX/YYY).
  //   mode=advanced → the full OAuth+API snippet used to upload click conversions.
  google_ads: [
    { key: 'developer_token', label: 'Developer Token', required: true, secret: true, advanced: true },
    { key: 'customer_id', label: 'Customer ID (xxx-xxx-xxxx)', required: true, advanced: true },
    { key: 'login_customer_id', label: 'Login Customer ID (optional)', advanced: true },
    { key: 'conversion_action', label: 'Conversion Action resource name', required: true, advanced: true },
    { key: 'client_id', label: 'OAuth Client ID', required: true, advanced: true },
    { key: 'client_secret', label: 'OAuth Client Secret', required: true, secret: true, advanced: true },
    { key: 'oauth_refresh_token', label: 'OAuth Refresh Token', required: true, secret: true, advanced: true },
  ],
  linkedin_capi: [
    { key: 'conversion_urn', label: 'Conversion URN (urn:lla:llaPartnerConversion:…)', required: true },
    { key: 'access_token', label: 'OAuth Access Token (rw_ads scope)', required: true, secret: true },
    { key: 'api_version', label: 'API version (default 202405)' },
  ],
  twitter_capi: [
    { key: 'pixel_id', label: 'Pixel ID', required: true },
    { key: 'access_token', label: 'OAuth Bearer Token', required: true, secret: true },
    { key: 'event_id', label: 'Event ID (tw-…, from X Events Manager)' },
  ],
  reddit_capi: [
    { key: 'pixel_id', label: 'Pixel ID (a2_…)', required: true },
    { key: 'access_token', label: 'Reddit Ads OAuth Token', required: true, secret: true },
  ],
  snapchat_capi: [
    { key: 'pixel_id', label: 'Snap Pixel ID', required: true },
    { key: 'access_token', label: 'Marketing API Token', required: true, secret: true },
    { key: 'test_event_code', label: 'Test event code (optional)' },
  ],
  pinterest_capi: [
    { key: 'ad_account_id', label: 'Ad Account ID', required: true },
    { key: 'access_token', label: 'Pinterest API Token (ads:write)', required: true, secret: true },
    { key: 'test_event_code', label: 'Test event code (optional)' },
  ],
  yandex_metrika: [
    { key: 'counter_id', label: 'Metrica counter ID', required: true },
    { key: 'oauth_token', label: 'OAuth token (Metrica access)', required: true, secret: true },
    { key: 'target', label: 'Goal identifier (Target; empty = event name)' },
    { key: 'client_id_param', label: 'Link parameter with Metrica ClientID (optional)' },
  ],
};

// Event a new forwarder starts with; '' = the platform takes no event name.
const DEFAULT_EVENT = {
  fb_capi: 'Purchase',
  google_ads: 'conversion',
  tiktok_events: 'Purchase',
  linkedin_capi: '',
  twitter_capi: '',
  reddit_capi: 'Purchase',
  snapchat_capi: 'PURCHASE',
  pinterest_capi: 'checkout',
  yandex_metrika: '',
};

// How each platform uses the event name (English keys, translated with t()).
const EVENT_HINT = {
  google_ads: "Tag mode (AW-…): the gtag event, usually conversion. API upload: not sent — the Conversion Action decides.",
  linkedin_capi: "Not sent: LinkedIn counts the conversion of the Conversion URN.",
  twitter_capi: "X counts the event from the Event ID field (tw-…). An event name that starts with tw- works too.",
  snapchat_capi: "Sent in capitals, e.g. PURCHASE, SIGN_UP.",
  pinterest_capi: "Pinterest names: checkout, lead, signup, add_to_cart, page_visit, custom. Purchase is sent as checkout.",
  yandex_metrika: "Goal identifier for Metrica when the Target field is empty.",
};

function ForwarderForm({ initial, onSave, campaigns }) {
  const { t } = useT();
  const [provider, setProvider] = useState(initial?.provider || 'fb_capi');
  const [name, setName] = useState(initial?.name || '');
  const [eventName, setEventName] = useState(initial ? (initial.event_name || '') : DEFAULT_EVENT.fb_capi);
  // A new forwarder follows the platform's default event until the name is edited.
  const changeProvider = (next) => {
    if (!eventName || eventName === (DEFAULT_EVENT[provider] ?? '')) setEventName(DEFAULT_EVENT[next] ?? 'Purchase');
    setProvider(next);
  };
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [config, setConfig] = useState(initial?.config || {});
  const [selectedCampaigns, setSelectedCampaigns] = useState(initial?.campaign_ids || []);
  const [statuses, setStatuses] = useState(initial?.statuses || ['sale']);
  // Status buttons show the same translated names as the rest of the app.
  const statusLabels = {
    lead: t('Lead'),
    sale: t('Sale'),
    reject: t('Reject'),
    upsell: t('Upsell'),
    registration: t('Registration'),
    deposit: t('Deposit'),
    trash: t('Trash'),
  };
  // Google Ads has two UIs: "simple" (just AW-XXX/YYY) and "advanced" (OAuth).
  // Default existing records without a mode to simple so the UX matches the
  // redesign — nothing changes server-side because advanced fields still work.
  const isGoogle = provider === 'google_ads';
  const mode = isGoogle ? (config.mode || 'simple') : null;
  const setMode = (m) => setConfig({ ...config, mode: m });

  const allFields = CONFIG_FIELDS[provider] || [];
  // For Google Ads, filter fields by current mode. Simple mode shows only the
  // non-advanced fields (we'll add an inline conversion-id input below).
  const fields = isGoogle
    ? allFields.filter(f => (mode === 'advanced' ? f.advanced : !f.advanced))
    : allFields;

  const submit = () => {
    if (!name.trim()) return toast.error(t('Name is required'));
    if (isGoogle && mode === 'simple' && !(config.conversion_id || '').trim()) {
      return toast.error(t('Conversion tag is required'));
    }
    if (!isGoogle || mode === 'advanced') {
      const missing = fields.filter(f => f.required && !config[f.key]).map(f => f.label);
      if (missing.length) return toast.error(t('Missing fields') + ': ' + missing.join(', '));
    }
    onSave({ name: name.trim(), provider, event_name: eventName.trim() || (DEFAULT_EVENT[provider] ?? 'Purchase'), is_active: isActive, config, campaign_ids: selectedCampaigns, statuses });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>{t('Provider')}</Label>
        <Select value={provider} onValueChange={changeProvider} disabled={!!initial}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(PROVIDER_LABEL).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>{t('Name')}</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={PROVIDER_LABEL[provider] || ''} />
      </div>
      <div>
        <Label>{t('Event name')}</Label>
        <Input value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder={DEFAULT_EVENT[provider] || t('Not sent')} />
        {EVENT_HINT[provider] && <p className="text-xs text-muted-foreground mt-1">{t(EVENT_HINT[provider])}</p>}
      </div>
      <div>
        <Label>{t('Fire on statuses')}</Label>
        <div className="flex flex-wrap gap-1 mt-1 text-xs">
          {['lead', 'sale', 'reject', 'upsell', 'registration', 'deposit', 'trash'].map((s) => {
            const on = statuses.includes(s);
            return (
              <button
                type="button"
                key={s}
                onClick={() => setStatuses(on ? statuses.filter((x) => x !== s) : [...statuses, s])}
                className={on ? 'px-3 py-1 rounded border bg-primary/15 border-primary/40 text-primary' : 'px-3 py-1 rounded border border-border text-muted-foreground'}
              >{statusLabels[s] || s}</button>
            );
          })}
        </div>
        <p className="text-[0.7rem] text-muted-foreground mt-1">{t('Only these conversion statuses are sent to the ad platform. Default: sale.')}</p>
      </div>

      {isGoogle && (
        <div className="pt-2 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <Label>{t('Mode')}</Label>
            <div className="flex gap-1 text-xs">
              <button
                type="button"
                onClick={() => setMode('simple')}
                className={`px-3 py-1 rounded border ${mode === 'simple' ? 'bg-primary/15 border-primary/40 text-primary' : 'border-border text-muted-foreground'}`}
              >{t('Simple')}</button>
              <button
                type="button"
                onClick={() => setMode('advanced')}
                className={`px-3 py-1 rounded border ${mode === 'advanced' ? 'bg-primary/15 border-primary/40 text-primary' : 'border-border text-muted-foreground'}`}
              >{t('Advanced')}</button>
            </div>
          </div>
          {mode === 'simple' ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {t('Paste the "send_to" value from your Google Ads conversion snippet (e.g. AW-123456789/AbCdEf). No OAuth needed. The tracker stores it and fires it via Final URL suffix or on-redirect gtag.')}
              </p>
              <Label>{t('Conversion tag')}<span className="text-destructive"> *</span></Label>
              <Input
                placeholder="AW-18102037113/bQN9COeIxZ4cEPnU3LdD"
                value={config.conversion_id || ''}
                onChange={(e) => setConfig({ ...config, conversion_id: e.target.value })}
              />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground mb-2">
              {t('OAuth-based Click Conversions Upload — requires developer token, customer ID and a refresh token. Use only if you need server-side uploads with gclid.')}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 pt-2 border-t border-border">
        {fields.map(f => (
          <div key={f.key}>
            <Label>{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>
            <Input
              type={f.secret ? 'password' : 'text'}
              value={config[f.key] || ''}
              onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })}
            />
          </div>
        ))}
      </div>
      {/* Campaign binding */}
      <div className="pt-2 border-t border-border">
        <Label>{t('Bind to campaigns')}</Label>
        <p className="text-xs text-muted-foreground mb-2">{t('Leave empty to fire on all campaigns')}</p>
        <Select
          value="_placeholder"
          onValueChange={(v) => {
            if (v !== '_placeholder' && !selectedCampaigns.includes(v)) {
              setSelectedCampaigns([...selectedCampaigns, v]);
            }
          }}
        >
          <SelectTrigger><SelectValue placeholder={t('Add campaign…')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_placeholder" disabled>{t('Add campaign…')}</SelectItem>
            {(campaigns || []).filter(c => !selectedCampaigns.includes(c.id)).map(c => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedCampaigns.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {selectedCampaigns.map(id => {
              const camp = (campaigns || []).find(c => c.id === id);
              return (
                <Badge key={id} variant="secondary" className="text-xs flex items-center gap-1">
                  {camp?.name || id.slice(0, 8)}
                  <button type="button" aria-label={t('Remove campaign')} title={t('Remove campaign')} onClick={() => setSelectedCampaigns(selectedCampaigns.filter(x => x !== id))} className="hover:text-destructive">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-border">
        <Label>{t('Active')}</Label>
        <Switch checked={isActive} onCheckedChange={setIsActive} />
      </div>
      <DialogFooter>
        <Button onClick={submit}>{t('Save')}</Button>
      </DialogFooter>
    </div>
  );
}

export default function ConversionForwarders() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [events, setEvents] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkCampaigns, setBulkCampaigns] = useState([]);
  const [bulkCreating, setBulkCreating] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/conversion-forwarders`);
      setItems(r.data || []);
    } finally { setLoading(false); }
  };

  const loadCampaigns = async () => {
    try {
      const r = await axios.get(`${API}/campaigns`);
      setCampaigns(r.data || []);
    } catch (_) { /* best effort */ }
  };

  useEffect(() => { load(); loadCampaigns(); }, []);

  const create = async (data) => {
    try {
      await axios.post(`${API}/conversion-forwarders`, data);
      toast.success(t('Forwarder created'));
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    }
  };
  const update = async (data) => {
    try {
      await axios.put(`${API}/conversion-forwarders/${editing.id}`, data);
      toast.success(t('Forwarder updated'));
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    }
  };
  const [deleteTarget, setDeleteTarget] = useState(null);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/conversion-forwarders/${id}`);
      setDeleteTarget(null);
      toast.success(t('Forwarder deleted'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    }
  };
  const test = async (id) => {
    try {
      const r = await axios.post(`${API}/conversion-forwarders/${id}/test`);
      if (r.data.ok) toast.success(t('Test event sent'));
      else toast.error(r.data.error || t('Test failed'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Test failed'));
    }
  };
  const loadEvents = async (id) => {
    const r = await axios.get(`${API}/conversion-forwarders/${id}/events`);
    setEvents(prev => ({ ...prev, [id]: r.data || [] }));
  };

  return (
    <div className="px-8 py-10 max-w-5xl">
      <XpnsrPageHeader
        kicker={t('Conversion API')}
        title={t('Conversion forwarders')}
        subtitle={t('Forward conversions server-to-server to ad networks (FB CAPI, Google Ads, TikTok). Fires automatically on every postback.')}
        actions={
          <div className="flex gap-2">
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
              <DialogTrigger asChild>
                <Button variant="outline"><ListPlus className="w-4 h-4 mr-1" /> {t('Bulk create')}</Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>{t('Bulk create Google Ads forwarders')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>{t('Conversion tags (one per line)')}</Label>
                    <p className="text-xs text-muted-foreground mb-1">{t('Paste AW-XXX/YYY tags, one per line. Each creates a separate forwarder.')}</p>
                    <Textarea
                      rows={8}
                      placeholder={'AW-18102037113/bQN9COeIxZ4cEPnU3LdD\nAW-29203148224/cRO0DPfJyZ5dFQoV4MeE\n...'}
                      value={bulkText}
                      onChange={(e) => setBulkText(e.target.value)}
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <Label>{t('Bind to campaigns')}</Label>
                    <p className="text-xs text-muted-foreground mb-1">{t('Leave empty to fire on all campaigns')}</p>
                    <Select
                      value="_placeholder"
                      onValueChange={(v) => {
                        if (v !== '_placeholder' && !bulkCampaigns.includes(v)) {
                          setBulkCampaigns([...bulkCampaigns, v]);
                        }
                      }}
                    >
                      <SelectTrigger><SelectValue placeholder={t('Add campaign…')} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_placeholder" disabled>{t('Add campaign…')}</SelectItem>
                        {campaigns.filter(c => !bulkCampaigns.includes(c.id)).map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {bulkCampaigns.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {bulkCampaigns.map(id => {
                          const camp = campaigns.find(c => c.id === id);
                          return (
                            <Badge key={id} variant="secondary" className="text-xs flex items-center gap-1">
                              {camp?.name || id.slice(0, 8)}
                              <button type="button" aria-label={t('Remove campaign')} title={t('Remove campaign')} onClick={() => setBulkCampaigns(bulkCampaigns.filter(x => x !== id))} className="hover:text-destructive">
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button
                      disabled={bulkCreating || !bulkText.trim()}
                      onClick={async () => {
                        const lines = bulkText.split('\n').map(l => l.trim()).filter(Boolean);
                        if (!lines.length) return;
                        setBulkCreating(true);
                        try {
                          const payload = lines.map((tag, i) => ({
                            name: `Google Ads #${items.length + i + 1} (${tag.split('/')[0]})`,
                            provider: 'google_ads',
                            config: { mode: 'simple', conversion_id: tag },
                            event_name: DEFAULT_EVENT.google_ads,
                            campaign_ids: bulkCampaigns,
                          }));
                          await axios.post(`${API}/conversion-forwarders/bulk`, { items: payload });
                          toast.success(t('{count} forwarders created').replace('{count}', lines.length));
                          setBulkOpen(false);
                          setBulkText('');
                          setBulkCampaigns([]);
                          load();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || t('Action failed'));
                        } finally {
                          setBulkCreating(false);
                        }
                      }}
                    >
                      {bulkCreating ? t('Creating…') : t('Create all')}
                    </Button>
                  </DialogFooter>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button><Plus className="w-4 h-4 mr-1" /> {t('New forwarder')}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t('New forwarder')}</DialogTitle></DialogHeader>
                <ForwarderForm onSave={create} campaigns={campaigns} />
              </DialogContent>
            </Dialog>
          </div>
        }
      />
      <div className="mb-6" />

      {loading ? (
        <p className="text-muted-foreground">{t('Loading…')}</p>
      ) : items.length === 0 ? (
        <Card>
          <CardContent style={{ padding: 0 }}>
            <XpnsrEmpty
              icon={Send}
              title={t('No forwarders yet')}
              subtitle={t('Add a forwarder to push conversions to ad networks automatically.')}
              cta={
                <button
                  type="button"
                  className="xp-btn xp-btn-primary"
                  onClick={() => setCreateOpen(true)}
                  data-testid="empty-create-forwarder"
                >
                  <Plus style={{ width: 12, height: 12 }} /> {t('Add forwarder')}
                </button>
              }
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map(fw => (
            <Card key={fw.id}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <CardTitle className="flex items-center gap-2">
                    <Send className="w-4 h-4" /> {fw.name}
                    <Badge variant="outline">{PROVIDER_LABEL[fw.provider] || fw.provider}</Badge>
                    {fw.is_active
                      ? <Badge className="bg-primary/15 text-primary border border-primary/30">{t('Active')}</Badge>
                      : <Badge variant="outline">{t('Inactive')}</Badge>}
                    {fw.campaign_ids?.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {fw.campaign_ids.length} {fw.campaign_ids.length === 1 ? t('campaign') : t('campaigns')}
                      </Badge>
                    )}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => test(fw.id)}>
                      <Beaker className="w-4 h-4 mr-1" /> {t('Send test')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => loadEvents(fw.id)}>
                      <Activity className="w-4 h-4 mr-1" /> {t('Events')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setEditing(fw)}>{t('Edit')}</Button>
                    <Button variant="outline" size="sm" aria-label={t('Delete forwarder')} title={t('Delete forwarder')} onClick={() => setDeleteTarget(fw)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <div className="flex flex-wrap gap-6">
                  <div><span className="uppercase text-[0.65rem] tracking-[0.2em] mr-2">{t('Sent')}</span>{fw.total_sent || 0}</div>
                  <div><span className="uppercase text-[0.65rem] tracking-[0.2em] mr-2">{t('Failed')}</span>{fw.total_failed || 0}</div>
                  <div><span className="uppercase text-[0.65rem] tracking-[0.2em] mr-2">{t('Event')}</span>{fw.event_name}</div>
                  {fw.last_sent_at && (
                    <div><span className="uppercase text-[0.65rem] tracking-[0.2em] mr-2">{t('Last sent')}</span>{new Date(fw.last_sent_at).toLocaleString()}</div>
                  )}
                </div>
                {fw.campaign_ids?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1 items-center">
                    <span className="uppercase text-[0.65rem] tracking-[0.2em] mr-1">{t('Campaigns')}</span>
                    {fw.campaign_ids.map(id => {
                      const camp = campaigns.find(c => c.id === id);
                      return <Badge key={id} variant="outline" className="text-xs">{camp?.name || id.slice(0, 8)}</Badge>;
                    })}
                  </div>
                )}
                {fw.last_error && (
                  <div className="mt-3 text-destructive text-xs flex items-start gap-1">
                    <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" /> {fw.last_error}
                  </div>
                )}
                {events[fw.id] && (
                  <div className="mt-4 border border-border rounded-md p-3 bg-surface-2 max-h-64 overflow-y-auto space-y-1">
                    {events[fw.id].length === 0 ? (
                      <p className="text-xs">{t('No events yet')}</p>
                    ) : events[fw.id].map(ev => (
                      <div key={ev.id} className="flex items-start gap-2 text-xs font-mono">
                        {ev.ok
                          ? <CheckCircle2 className="w-3 h-3 text-primary mt-0.5 shrink-0" />
                          : <AlertCircle className="w-3 h-3 text-destructive mt-0.5 shrink-0" />}
                        <span className="text-muted-foreground shrink-0">{new Date(ev.created_at).toLocaleTimeString()}</span>
                        <span className="shrink-0">{ev.conversion_id?.slice(0, 8)}</span>
                        <span className="truncate">{ev.error || 'OK'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('Edit forwarder')}</DialogTitle></DialogHeader>
          {editing && <ForwarderForm initial={editing} onSave={update} campaigns={campaigns} />}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Delete forwarder')}
        description={t('This will permanently delete this conversion forwarder.')}
        confirmLabel={t('Delete')}
        onConfirm={() => remove(deleteTarget?.id)}
      />
    </div>
  );
}
